import React, { useState } from 'react';
import SETTING_KEYS from '../../constants/setting-keys';
import styles from './Conditional.module.css';
import WaitingPeriod from './WaitingPeriod';
import FieldStrength from './FieldStrength';
import SpatialGradient from './SpatialGradient';
import Artefacts from './Artefacts';
import { SAR } from './SpecificAbsorptionRate';
import Temperature from './Temperature';
import ConditionalTextField from './ConditionalTextField';
import MriArc from './MriArc';
import SlewRate from './SlewRate';
import Prep from './Prep';
import { RegionalInfo, useImplantContext } from '../../store/implant-context';
import SelectItem from '../general-components/SelectItem';
import RichText from '../general-components/RichText';
import Button, { ButtonType } from '../general-components/Button';
import BackgroundContainer from '../ui-components/BackgroundContainer';
import { getConditionalBorderColor } from '../edit-implant/regional/getConditionalBorderColor';


const Conditional = ({
    index,
    settings,
    conditionals,
    fieldStrengths,
    autofilledFields,
    showWarningIcon,
    itemsRef,
    onSetShowWarningIcon,
    onSetAutoFilledFields,
    onFieldStrengthChange,
    onUpdateConditionalField,
    onResetHighlights,
    hasDeleteButton = true,
}: {
    index?: number;
    settings: any[];
    conditionals: any;
    fieldStrengths: any;
    autofilledFields?: any;
    showWarningIcon: any;
    itemsRef?: any;
    onSetShowWarningIcon?: Function;
    onSetAutoFilledFields?: Function;
    onFieldStrengthChange: (value: any, checked: boolean) => void;
    onUpdateConditionalField: (fieldName: any, value: any) => void;
    onResetHighlights?: Function;
    hasDeleteButton?: boolean;
}) => {
    const [fileExtractResponse, setFileExtractResponse] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const conditionalId = conditionals._id || conditionals.id || index;
    const renderInput = (d: string, i: number) => {
        const value = conditionals[d];

        let autofilled = autofilledFields === undefined ? '' : autofilledFields;

        switch (d) {
            case SETTING_KEYS.fieldStrength:
                return (
                    <FieldStrength
                        value={value}
                        fieldStrengths={fieldStrengths}
                        onFieldStrengthChange={onFieldStrengthChange}
                    />
                );
            case SETTING_KEYS.waitingPeriod:
                return (
                    <WaitingPeriod
                        d={d}
                        value={value}
                        conditionals={conditionals}
                        onUpdateConditionalField={onUpdateConditionalField}
                    />
                );
            case SETTING_KEYS.spatialGradient:
                return (
                    <SpatialGradient
                        d={d}
                        value={value}
                        autofilledFields={autofilled}
                        onUpdateConditionalField={onUpdateConditionalField}
                        onResetHighlights={onResetHighlights}
                    />
                );
            case SETTING_KEYS.artefacts:
                return (
                    <Artefacts
                        d={d}
                        value={value}
                        autofilledFields={autofilled}
                        showWarningIcon={showWarningIcon}
                        onUpdateConditionalField={onUpdateConditionalField}
                        onResetHighlights={onResetHighlights}
                        onSetAutoFilledFields={onSetAutoFilledFields}
                        onSetShowWarningIcon={onSetShowWarningIcon}
                    />
                );
            case SETTING_KEYS.sar:
                return (
                    <SAR
                        d={d}
                        value={value}
                        autofilledFields={autofilled}
                        showWarningIcon={showWarningIcon}
                        onUpdateConditionalField={onUpdateConditionalField}
                        onResetHighlights={onResetHighlights}
                        onSetAutoFilledFields={onSetAutoFilledFields}
                        onSetShowWarningIcon={onSetShowWarningIcon}
                    />
                );
            case SETTING_KEYS.temp:
                return (
                    <Temperature
                        d={d}
                        value={value}
                        autofilledFields={autofilled}
                        onUpdateConditionalField={onUpdateConditionalField}
                        onResetHighlights={onResetHighlights}
                    />
                );
            case SETTING_KEYS.mriArc:
                return (
                    <MriArc
                        description={conditionals[d]}
                        d={d}
                        onUpdateConditionalField={onUpdateConditionalField}
                    />
                );
            case SETTING_KEYS.slewRate:
                return (
                    <SlewRate
                        d={d}
                        value={value}
                        onUpdateConditionalField={onUpdateConditionalField}
                    />
                );
            case SETTING_KEYS.brms:
                return (
                    <ConditionalTextField
                        d={d}
                        value={value}
                        onUpdateConditionalField={onUpdateConditionalField}
                    />
                );
            case SETTING_KEYS.coilType:
                return (
                    <ConditionalTextField
                        d={d}
                        value={value}
                        onUpdateConditionalField={onUpdateConditionalField}
                    />
                );
            case SETTING_KEYS.prep:
                return (
                    <Prep
                        description={conditionals[d]}
                        d={d}
                        onUpdateConditionalField={onUpdateConditionalField}
                    />
                );
            default:
                return (
                    <textarea
                        ref={(el) => (itemsRef.current[i] = el)}
                        name={d}
                        value={conditionals[d]}
                        onChange={(e) =>
                            onUpdateConditionalField(d, e.target.value)
                        }
                    />
                );
        }
    };

    const renderColumns = () => {
        return settings.map((key: any, i: any) => (
            <div className={styles.column}>
                <React.Fragment key={i}>
                    <label className={styles.infoLabel}>
                        {key + (key === SETTING_KEYS.fieldStrength ? ' *' : '')}
                    </label>
                    {renderInput(key, i)}
                </React.Fragment>
            </div>
        ));
    };

    const {
        regionalInfos,
        updateRegionalInfoField,
        duplicateConditional,
        deleteConditional,
    } = useImplantContext();

    const deleteConditionalAndUpdateRegionalInfo = () => {
        if (hasDeleteButton) {
            deleteConditional(conditionalId);
            // Find and update all regional infos that reference this conditional
            regionalInfos.forEach(
                (regionalInfo: any, regionalInfoIndex: number) => {
                    if (
                        regionalInfo.conditionals === conditionalId ||
                        (typeof index !== 'undefined' &&
                            regionalInfo.conditionals === index)
                    ) {
                        updateRegionalInfoField(
                            regionalInfoIndex,
                            'conditionals',
                            null
                        );
                    }
                }
            );
        }
    };

    const handleRegionSelect = (
        regionalInfoIndex: number,
        value: any,
        checked: boolean
    ) => {
        if (checked) {
            updateRegionalInfoField(
                regionalInfoIndex,
                'conditionals',
                conditionalId
            );
        } else {
            updateRegionalInfoField(regionalInfoIndex, 'conditionals', null);
        }
    };

    const borderColor = getConditionalBorderColor(conditionalId);


    const fieldMapping: Record<string, string> = {
        "Static magnetic field": SETTING_KEYS.fieldStrength,
        "Waiting period": SETTING_KEYS.waitingPeriod,
        "Spatial gradient": SETTING_KEYS.spatialGradient,
        "Artefacts": SETTING_KEYS.artefacts,
        "SAR": SETTING_KEYS.sar,
        "Temperature": SETTING_KEYS.temp,
        "MRI architecture": SETTING_KEYS.mriArc,
        "Slew rate (Gr)": SETTING_KEYS.slewRate,
        "B1+rms": SETTING_KEYS.brms,
        "Coil type": SETTING_KEYS.coilType,
        "Preparation": SETTING_KEYS.prep,
    };

    const fillForm = () => {
        if (!fileExtractResponse) return;
        try {
            const data = JSON.parse(fileExtractResponse);
            if (typeof data !== 'object') {
                console.error("Parsed data is not an object:", data);
                console.error("Data is: ", typeof data);
                return;
            }
            Object.entries(fieldMapping).forEach(([jsonKey, settingKey]) => {
                if (data[jsonKey] !== undefined && data[jsonKey] !== null) {
                    onUpdateConditionalField(settingKey, data[jsonKey]);
                }
            });
        } catch (e) {
            console.error("Error parsing JSON to fill form", e);
        }
    };
    

    return (
        <BackgroundContainer borderColor={borderColor}>
            <div className={styles.conditionals}>
                <h3>
                    Conditionals {index !== undefined ? `(${index + 1})` : ''}
                </h3>
                {index !== undefined && (
                    <div
                        style={{
                            display: 'flex',
                            gap: '10px',
                            marginBottom: '10px',
                        }}
                    >
                        <Button
                            type={ButtonType.secondary}
                            onClick={() => duplicateConditional(conditionalId)}
                        >
                            Duplicate Section
                        </Button>
                        {hasDeleteButton && (
                            <Button
                                type={ButtonType.cancel}
                                onClick={() =>
                                    deleteConditionalAndUpdateRegionalInfo()
                                }
                            >
                                Remove Section
                            </Button>
                        )}
                    </div>
                )}
                <div style={{ marginBottom: '10px' }}>
                    {index !== undefined && (
                        <>
                            <label>Assign to Regional Info</label>
                            {conditionalId !== undefined &&
                                regionalInfos
                                    .filter(
                                        (regionalInfo: any) =>
                                            regionalInfo?.mriStatus === 2
                                    )
                                    .map(
                                        (
                                            regionalInfo: any,
                                            regionalInfoIndex: number
                                        ) => {
                                            const isAssigned =
                                                regionalInfo.conditionals ===
                                                conditionalId;
                                            console.log(
                                                regionalInfo.regions,
                                                regionalInfo.conditionals
                                            );
                                            return (
                                                <>
                                                    <SelectItem
                                                        checked={isAssigned}
                                                        disabled={
                                                            regionalInfo.conditionals !==
                                                                null &&
                                                            regionalInfo.conditionals !==
                                                                undefined &&
                                                            !isAssigned
                                                        }
                                                        key={regionalInfoIndex}
                                                        value={regionalInfo?.regions?.join(
                                                            ', '
                                                        )}
                                                        onChange={(
                                                            value: any,
                                                            checked: boolean
                                                        ) =>
                                                            handleRegionSelect(
                                                                regionalInfoIndex,
                                                                regionalInfo,
                                                                checked
                                                            )
                                                        }
                                                        type="checkbox"
                                                    />
                                                </>
                                            );
                                        }
                                    )}
                        </>
                    )}
                </div>
                <div className={styles.flex}>{renderColumns()}</div>
            </div>
        </BackgroundContainer>
    );
};

export default Conditional;
